import * as z from "zod";
import { NOT_FOUND_MESSAGES, userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";

const defaultHomeViews = new Set([
  "branches",
  "chats",
  "favorites",
  "monitor",
  "recents",
  "spaces",
]);

const themePreferenceSchema = z.enum(["light", "dark", "system"]);
export type ThemePreference = z.infer<typeof themePreferenceSchema>;

export const setUserPreference = mutator({
  schema: z.object({
    spaceSortOrder: z.enum(["alphabetical", "recency"]).optional(),
    defaultHome: z.string().optional(),
    soundClicks: z.enum(["on", "off"]).optional(),
    soundChat: z.enum(["on", "off"]).optional(),
    soundWorkflow: z.enum(["on", "off"]).optional(),
    theme: themePreferenceSchema.optional(),
    defaultSpace: z.string().nullable().optional(),
    showCode: z.enum(["on", "off"]).optional(),
    browserNotifications: z.enum(["on", "off"]).optional(),
    planModeDefault: z.enum(["on", "off"]).optional(),
    planModeChatDefault: z.enum(["on", "off"]).optional(),
    planModeWorkflowDefault: z.enum(["on", "off"]).optional(),
    planModeBranchDefault: z.enum(["on", "off"]).optional(),
    showFeatureFlagsNav: z.enum(["on", "off"]).optional(),
    workflowTabOrder: z.string().nullable().optional(),
    navConfig: z.string().nullable().optional(),
    follow3bByDefault: z.enum(["on", "off"]).optional(),
    suggestFollow3b: z.enum(["on", "off"]).optional(),
    aiProviderId: z.string().nullable().optional(),
    chatModel: z.string().nullable().optional(),
    followUpBehavior: z.enum(["queue", "steer"]).optional(),
  }),
  resolve: {
    defaultSpaceRef: {
      kind: "space",
      from: "defaultSpace",
      optional: true,
    },
  },
  permissions: { none: "writes only the calling user’s own preference row" },
  fn: async ({ tx, ctx, args }) => {
    const { userId } = ctx;
    if (args.defaultHome && !defaultHomeViews.has(args.defaultHome)) {
      const [space] = await tx.run(
        zql.spaces.where("id", args.defaultHome).where("tenantId", ctx.tenantId)
      );
      if (!space) throw userFacingError(NOT_FOUND_MESSAGES.space);
    }
    const { defaultSpaceRef: _defaultSpaceRef, ...preferences } = args;
    await tx.mutate.userPreferences.upsert({
      userId,
      tenantId: ctx.tenantId,
      ...preferences,
    });
  },
});
